
export function asyncDelay(n = 500, withError = false) {
  return new Promise((resolve, reject) => {
    setTimeout(withError ? reject : resolve, n);
  });
}

// Request persistent storage for site
export async function requestPersistedStorage() {
  // console.log('Requesting persistent storage');
  if (navigator.storage && navigator.storage.persist) {
    // console.log('awaiting Requesting persistent storage');
    const isPersisted = await Promise.race([navigator.storage.persist(), asyncDelay(200)]);
    // console.log(`Persisted storage granted: ${isPersisted}`);
    return isPersisted;
  }

  return false;
}

export function padded(value) {
  return value.toString().padStart(2, '0');
}

// Overflow hours
export function durationToParts(duration) {
  const seconds = Math.floor(duration / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  return {
    hours: hours,
    minutes: minutes % 60,
    seconds: seconds % 60,
  };
}

export function durationToHuman(duration, { size =  3, invert = false } = {} ) {
  const parts = durationToParts(Math.abs(duration));
  let sign = duration < 0 ? '-' : '';

  if (invert) sign = duration < 0 ? '+' : '';

  const partsArray = Object.values(parts).map(p => padded(p)).slice(0, size);
  const formatted = partsArray.join(':');

  return `${sign}${formatted}`;
}

export function parseDate(value) {
  if (!value) return value;
  if (value instanceof Date) return value;

  const parsed = new Date(value);

  if (isNaN(parsed)) {
    return null;
  }

  return parsed;
}

export function downloadContent(text, fileName, contentType = 'text/plain') {
  const blob = new Blob([text], { type: contentType });

  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.setAttribute('style', 'display: none');

  document.body.appendChild(a);
  a.click();

  // Cleanup
  a.remove();
  URL.revokeObjectURL(url);
};

export async function shareContent(text, fileName, contentType = 'text/plain') {

  if(!fileName.endsWith('.txt')) throw new Error('File name must end with .txt');

  const blob = new Blob([text], { type: contentType });
  const file = new File([blob], fileName, { type: contentType });

  const shareData = {
    files: [file]
  };

   if (!navigator.canShare) {
    alert('Sharing is not supported on this device or browser.');
    return;
  }


  try {
    await navigator.share(shareData);
    return true;
  } catch (error) {
    console.error('Error sharing content:', error);
    if (error.name === 'AbortError') return;

    alert(`${error.name}: ${error.message}`);
  }

  return false;
}


export async function hasPermission(permission) {
  if (!navigator.permissions) return;

  const permissionStatus = await navigator.permissions.query({ name: permission });
  return permissionStatus.state === 'granted';
}


export function dateToRelativeName(dateOrTimestamp) {
  const d = new Date(dateOrTimestamp);
  if (!d) return;

  const n = new Date();
  const isToday = d.getDate() === n.getDate() && d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear();
  const isYesterday = d.getDate() === n.getDate() - 1 && d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear();
  const isTomorrow = d.getDate() === n.getDate() + 1 && d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear();

  if(isToday) return 'Today';
  if(isTomorrow) return 'Tomorrow';
  if(isYesterday) return 'Yesterday';
}

const relativeFormatter = new Intl.DateTimeFormat('hr', {
  hour: "numeric",
  minute: "numeric"
});

const dateFormatters = new WeakMap();

function adaptiveFormatOptions(date) {
  if(!date) return;

  const n = new Date();
  const isSameYear = date.getFullYear() === n.getFullYear();
  const isSameMonth = isSameYear && date.getMonth() === n.getMonth();

  if(isSameMonth) {
    return {
      weekday: "short",
      day: "2-digit",
      hour: "numeric",
      minute: "numeric"
    };
  }

  return {
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "numeric"
  }
}

export function dateFormat(dateOrTimestamp, config = {}) {
  if (!dateOrTimestamp) return;
  const date = new Date(dateOrTimestamp);

  const relativeName = dateToRelativeName(date);
  if(relativeName) {
    return `${relativeName} ${relativeFormatter.format(date)}`;
  }

  const options = Object.assign(adaptiveFormatOptions(date), config);

  const formatter = dateFormatters.get(options);

  if (formatter) return formatter.format(date);

  const newFormatter = new Intl.DateTimeFormat('en-GB', options);
  dateFormatters.set(options, newFormatter);

  return newFormatter.format(date);
}
// Formatter: YYYY-MM-DDTHH:mm
export function dateToInput(date) {

  if (!date) return;

  const year = date.getFullYear();

  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

export function defineAttributes(object, attributes) {
  for (const attr of attributes) {
        Object.defineProperty(object.prototype, attr, {
      get() {
        return this.attributes[attr];
      },
      set(value) {

        if (attr.endsWith('At')) {
          value = parseDate(value);
        }

        if(this.attributes[attr] !== value) {
          this.isDirty = true;
          this.changes.set(attr, [this.attributes[attr], value]);
        };
        this.attributes[attr] = value;
      }
    });
  }
}

export function uniq(array) {
  return [...new Set(array)];
}

export function objectDiff({current, next}) {
  const diff = {};

  for (const key in current) {
    const nextValue = next[key];
    const currentValue = current[key];

    // Ignore undefined values - useful when syncing from server and attribute is not present
    // as it's local only
    if(nextValue === undefined) continue;
    if (currentValue === nextValue) continue;
    if (currentValue instanceof Date && currentValue.toJSON() === nextValue) continue;

    if(typeof nextValue === 'object') {
      const subDiff = objectDiff({current: currentValue, next: nextValue});
      if(Object.keys(subDiff).length) {
        // console.log('DIFF OBJECT', key, subDiff);
        diff[key] = subDiff;
      }
    } else {
      // console.log('DIFF VALUE', key, currentValue, nextValue);
      diff[key] = nextValue;

    }

  }

  for(const key in next) {
    if (key in current) continue;

    const nextValue = next[key];
    const currentValue = current[key];

    if(nextValue === currentValue) continue;

    // console.log('DIFF REVERSE', {key, currentValue, nextValue});

    diff[key] = nextValue;
  }

  return diff;
}

export function fluxHuman(value) {
  const v = value || 0;

  const human = durationToHuman(Math.abs(v), { size: 2 });

  return v >= 0 ? `+${human}` : `-${human}`;
}

export function cssVar(variableName) {
  const value = getComputedStyle(document.documentElement).getPropertyValue(variableName);
  return value ? value.trim() : null;
}
